import React from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import {
  CalendarContainer,
  CalendarHeader,
  MonthNavButton,
  MonthTitle,
  DaysGrid,
  DayHeader,
  CalendarGrid,
  DayCell,
  DayButton,
  SlotIndicator,
  CalendarLegend,
  LegendItem,
  LegendBox,
  LegendText,
  SlotIndicatorWrapper,
} from './styled'
import ChevronLeft from '../../assets/svgs/ChevronLeft'
import ChevronRight from '../../assets/svgs/ChevronRight'
import { getDaysInMonth, getFirstDayOfMonth } from './utils'

function SlotCalendar({ availableDates, selectedDate, onSelectDate }) {
  const { t, i18n } = useTranslation()
  const [currentMonth, setCurrentMonth] = React.useState(new Date())

  const nextMonth = () =>
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1),
    )

  const prevMonth = () =>
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1),
    )

  const renderDays = () => {
    const days = t('calendar.dayNames', { returnObjects: true })
    return (
      <DaysGrid>
        {days.map((day) => (
          <DayHeader key={day}>{day}</DayHeader>
        ))}
      </DaysGrid>
    )
  }

  const renderCells = () => {
    const year = currentMonth.getFullYear()
    const month = currentMonth.getMonth()
    const daysInMonth = getDaysInMonth(year, month)
    const firstDayOfMonth = getFirstDayOfMonth(year, month)
    const cells = []

    for (let i = 0; i < firstDayOfMonth; i++) {
      cells.push(
        <DayCell key={`empty-${i}`}>
          <div
            style={{ textAlign: 'center', padding: '0.5rem', color: '#d1d5db' }}
          />
        </DayCell>,
      )
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day)
      const dateString = date.toDateString()
      const dayAvailability = availableDates.find(
        (d) => d.date.toDateString() === dateString,
      )
      const isAvailable = dayAvailability?.isAvailable ?? false
      const hasSlots =
        dayAvailability?.slots.some((slot) => slot.isAvailable) ?? false
      const isSelected =
        selectedDate && selectedDate.toDateString() === dateString
      const isToday = new Date().toDateString() === dateString

      cells.push(
        <DayCell key={day}>
          <DayButton
            isSelected={isSelected}
            isAvailable={isAvailable}
            hasSlots={hasSlots}
            isToday={isToday}
            onClick={() => isAvailable && hasSlots && onSelectDate(date)}
          >
            <span>{day}</span>
            {isAvailable && hasSlots && !isSelected && <SlotIndicator />}
          </DayButton>
        </DayCell>,
      )
    }

    return <CalendarGrid>{cells}</CalendarGrid>
  }

  const formatMonthYear = () => {
    try {
      const locale = t('calendar.locale')
      return currentMonth.toLocaleDateString(locale, {
        month: 'long',
        year: 'numeric',
      })
    } catch {
      try {
        return currentMonth.toLocaleDateString(i18n.language, {
          month: 'long',
          year: 'numeric',
        })
      } catch {
        return currentMonth.toLocaleDateString(undefined, {
          month: 'long',
          year: 'numeric',
        })
      }
    }
  }

  return (
    <CalendarContainer>
      <CalendarHeader>
        <MonthNavButton onClick={prevMonth}>
          <ChevronLeft width={20} height={20} />
        </MonthNavButton>
        <MonthTitle>{formatMonthYear()}</MonthTitle>
        <MonthNavButton onClick={nextMonth}>
          <ChevronRight width={20} height={20} />
        </MonthNavButton>
      </CalendarHeader>
      {renderDays()}
      {renderCells()}
      <CalendarLegend>
        <LegendItem>
          <LegendBox variant="selected">15</LegendBox>
          <LegendText>{t('general.selected')}</LegendText>
        </LegendItem>
        <LegendItem>
          <LegendBox variant="unavailable">15</LegendBox>
          <LegendText>{t('calendar.unavailable')}</LegendText>
        </LegendItem>
        <LegendItem>
          <SlotIndicatorWrapper>
            <LegendBox variant="hasSlots">15</LegendBox>
            <SlotIndicator />
          </SlotIndicatorWrapper>
          <LegendText>{t('calendar.hasSlots')}</LegendText>
        </LegendItem>
      </CalendarLegend>
    </CalendarContainer>
  )
}

SlotCalendar.propTypes = {
  availableDates: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.instanceOf(Date).isRequired,
      isAvailable: PropTypes.bool.isRequired,
      slots: PropTypes.arrayOf(
        PropTypes.shape({
          isAvailable: PropTypes.bool.isRequired,
        }),
      ).isRequired,
    }),
  ).isRequired,
  selectedDate: PropTypes.instanceOf(Date),
  onSelectDate: PropTypes.func.isRequired,
}

SlotCalendar.defaultProps = {
  selectedDate: null,
}

export default SlotCalendar
